import React, { useEffect, useState } from "react";
import { Tag, Tooltip, message, notification } from "antd";
import { useRecoilState } from "recoil";
import { ColumnsType } from "antd/es/table";
import { breadcrumbState } from "../../../lib/stores/breadcrumb";
import getApiEndpoints from "../../../services/endpoints";
import { del, deleteMethod, get, post } from "../../../services/apiService";
import CommonTable from "../../../lib/components/Table/table";
import {
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import DiscardChangeModal from "../../../lib/components/modals/discard-change-modal";
import { useNavigate } from "react-router-dom";
import { companyState } from "../../../lib/stores/companyId";
import CopyClipBoard from "../../../lib/components/copyClipBoard/page";
import MapIcon from "../../../lib/components/MapLocator/page";
import PricePlans from "../PricePlans/page";

interface DataType {
  title: string;
  width: number;
  dataIndex: string;
  key: string;
  sorter: boolean;
}

// Function to check if a string is an ISO 8601 date
const isISO8601Date = (value: any) => {
  const date = new Date(value);
  return !isNaN(date.getTime());
};

const renderStatus = (value?: any, key?: any, record?: any) => {
  if (key === "userName") {
    if (record?.fcmDataID?.userName) {
      return (
        <Tooltip title={record.fcmDataID.userName}>
          <div>{record.fcmDataID.userName}</div>
        </Tooltip>
      );
    }
  }
  if (key === "taxInclusive") {
    const color = record.taxInclusive === true ? "green" : "volcano";
    return (
      <div>
        <Tag color={color}>
          {record.taxInclusive === true ? "True" : "False"}
        </Tag>
      </div>
    );
  }
  if (key === "locationId") {
    if (value !== null) {
      return <CopyClipBoard text={value} />;
    } else {
      return "NA";
    }
  }
  if (key === "locationAddress") {
    if (value !== null) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Tooltip title={value}>
            {/* <div>{value}</div> */}
            <div className="ellipsis-cell">{value}</div>
          </Tooltip>
          <MapIcon latitude={record?.latitude} longitude={record?.longitude} />
        </div>
      );
    } else {
      return "NA";
    }
  }
  if (key === "variantSize") {
    return (
      <Tooltip title={value}>
        <div style={{ backgroundColor: record?.modified ? "#faaf69" : "" }}>
          {value}
        </div>
      </Tooltip>
    );
  }
  if (key === "updatedAt") {
    if (value !== null && isISO8601Date(value)) {
      const date = new Date(value);
      return date.toLocaleDateString();
    }
  }

  // Handling other truthy values with ellipsis and tooltip
  if (value) {
    return (
      <Tooltip title={value}>
        <div>{value}</div>
      </Tooltip>
    );
  }

  // Default case for falsy values other than boolean false
  return "NA";
};

const Variants: React.FC = () => {
  const navigate = useNavigate();
  const [, setBreadCrumb] = useRecoilState(breadcrumbState);
  const [companyId] = useRecoilState(companyState);
  const [rowData, setRowData] = useState<any>();
  const user: any =
    localStorage.getItem("user") || sessionStorage.getItem("user");
  const userData: any = JSON.parse(user);
  const userRole = userData?.roles[0];
  const apiEndpoints = getApiEndpoints(userRole);
  const initialValues = { Machine: "all", Location: "all" };

  useEffect(() => {
    setBreadCrumb([
      { label: "Masters", href: "/dashboard" },
      { label: "Variants", href: "/variants" },
    ]);
  }, [setBreadCrumb]);

  const fetchVariants = async () => {
    try {
      const response = await get(apiEndpoints.variants);
      if (response.status === 200) {
        setRowData(response?.data);
      }
    } catch (error) {
      console.log(error);
      message.error("Error while fetching");
    }
  };

  useEffect(() => {
    fetchVariants();
  }, []);

  const handleVariants = (record: any) => {
    navigate(`/variants/variant/${record.itemId}`);
  };

  const handleEdit = (record: any) => {
    navigate(`/variant/${record.variantId}`);
  };

  const handleDeleteConfirmation = async (itemId: string) => {
    try {
      if (itemId) {
        const response = await deleteMethod(
          `${apiEndpoints.itemvariantsVariant}/${itemId}`
        );
        if (response.status === 200) {
          notification.success({
            message: "Successfully Deleted",
          });
          await fetchVariants();
        }
      }
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleDelete = (record: any) => {
    DiscardChangeModal(
      "Click OK to Confirm to Delete.",
      async () => {
        handleDeleteConfirmation(record?.variantId);
      },
      () => {},
      { title: "Are you sure you want to Delete ?" }
    );
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Variant Id",
      dataIndex: "variantId",
      key: "variantId",
      sorter: (a: any, b: any) =>
        a.variantId
          .toString()
          .toLowerCase()
          .localeCompare(b.variantId.toString().toLowerCase()),
      width: 25,
      // render: renderStatus,
      render: (value) => renderStatus(value, "variantId"),
    },
    {
      title: "Variant Name",
      dataIndex: "variantName",
      key: "variantName",
      sorter: (a: any, b: any) =>
        a.variantName
          .toString()
          .toLowerCase()
          .localeCompare(b.variantName.toString().toLowerCase()),
      width: 30,
      render: renderStatus,
    },
    {
      title: "Variant Size",
      dataIndex: "variantSize",
      key: "variantSize",
      sorter: (a: any, b: any) =>
        (a?.variantSize?.toString() ?? "")
          .toLowerCase()
          .localeCompare((b?.variantSize?.toString() ?? "").toLowerCase()),
      width: 30,
      // render: renderStatus,
      render: (value) => renderStatus(value, "variantSize"),
    },
    {
      title: "Item Name",
      dataIndex: "itemName",
      key: "itemName",
      sorter: (a: any, b: any) =>
        a.itemName
          .toString()
          .toLowerCase()
          .localeCompare(b.itemName.toString().toLowerCase()),
      width: 25,
      render: renderStatus,
    },
    {
      title: "Description",
      dataIndex: "variantDesc",
      key: "variantDesc",
      sorter: (a: any, b: any) =>
        a.desc
          .toString()
          .toLowerCase()
          .localeCompare(b.variantDesc.toString().toLowerCase()),
      width: 40,
      render: (value, record) => renderStatus(value, "itemDescription", record),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      sorter: (a: any, b: any) =>
        a.unitPrice
          .toString()
          .toLowerCase()
          .localeCompare(b.unitPrice.toString().toLowerCase()),
      width: 25,
      render: renderStatus,
    },
    {
      title: "Tax Rate",
      dataIndex: "taxRate",
      key: "taxRate",
      sorter: (a: any, b: any) =>
        a.taxRate
          .toString()
          .toLowerCase()
          .localeCompare(b.taxRate.toString().toLowerCase()),
      width: 25,
      render: renderStatus,
    },
    {
      title: "Tax Inclusive",
      dataIndex: "taxInclusive",
      key: "taxInclusive",
      sorter: (a: any, b: any) =>
        a.taxInclusive
          .toString()
          .toLowerCase()
          .localeCompare(b.taxInclusive.toString().toLowerCase()),
      width: 30,
      render: (value, record) => renderStatus(value, "taxInclusive", record),
    },

    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 30,
      render: (record) => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          &nbsp; &nbsp;&nbsp;
          <Tooltip title="Edit">
            <EditOutlined onClick={() => handleEdit(record)} />
          </Tooltip>{" "}
          &nbsp; &nbsp;&nbsp;
          <Tooltip title="Delete">
            <DeleteOutlined onClick={() => handleDelete(record)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <CommonTable
        routePath="/variant/add"
        showDates={false}
        showDropDowns={false}
        pageName="Mobile-Devices"
        dataSource={rowData}
        columns={columns}
        initialValues={initialValues}
        onFinish={fetchVariants}
      />
    </>
  );
};

export default Variants;
